import {Component, computed, DestroyRef, inject, OnInit, signal, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ViewportScroller} from '@angular/common';
import {SubmitForm} from '../../sdk/interface/form/submitForm';
import {ActivatedRoute, Router} from '@angular/router';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {AlertService} from '../../sdk/service/alert.service';
import {finalize, map as rxMap, switchMap} from 'rxjs/operators';
import {forkJoin, Observable, of, take, timer} from 'rxjs';
import {
    Bank,
    BankDetails,
    BusinessDetails,
    BusinessECommerceStore,
    BusinessECommerceStoreInput,
    BusinessNature,
    BusinessType,
    ECommerceStore,
    FicaDetails,
    KycStatus,
    MerchantOauthClientSecretResetRequest,
    PublicService,
    Response,
    VerificationStatus,
    WebBusinessService,
    Webhook,
    WebhookCreate,
    WebhookType,
    WebhookUpdate
} from 'web-angular-client';
import {LayoutService} from '../layout/layout.service';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {SyncValidators} from '../../sdk/validator/SyncValidators';
import {requireWhileSelected} from '../../sdk/validator/require-while-selected';
import {ConfirmationService} from '../../sdk/service/confirmation.service';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {EcommerceVerificationService} from '../../sdk/service/ecommerce-verification.service';
import {DropdownItem} from '../../sdk/interface/data/DropdownItem';
import {ECommerceStoreVerificationDisplay} from '../../sdk/interface/ecommerce-store-verification-display';
import {getECommerceStoreVerification as resolveStoreVerification}
    from '../../sdk/util/ecommerce/ecommerce-store-verification';
import {ConstantsUtil} from '../../sdk/util/constantsUtil';
import {DialogService} from 'primeng/dynamicdialog';
import {ResetMerchantClientSecretComponent} from '../reset-merchant-client-secret/reset-merchant-client-secret.component';
import {copyToClipboard as writeToClipboard} from '../../sdk/util/clipboard';
import ResponseEnum = Response.ResponseCodeEnum;

@Component({
    selector: 'app-account-settings',
    templateUrl: './account-settings.component.html',
    styleUrls: ['./account-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AccountSettingsComponent extends SubmitForm implements OnInit
{
    banks: Bank[];
    availableECommerceStores!: ECommerceStore[];
    businessECommerceStores!: BusinessECommerceStore[];
    isSoleProprietor: boolean;
    logoFile: File;
    logoImage: string = null;
    ficaDetailsDTO: FicaDetails = null;
    businessDetailsDTO: BusinessDetails = null;
    bankDetailsDTO: BankDetails = null;
    webhooks = signal<Webhook[]>([]);
    webhookForm: FormGroup<{ url: FormControl<string> }>;
    eCommerceStoreForm: FormGroup<{
        selectedECommerceStore: FormControl<ECommerceStore | null>;
        eCommerceStoreDomain: FormControl<string>;
        eCommerceStoreDescription: FormControl<string>;
    }>;

    canManageECommerceStoreCredentials = false;
    isLoadingWebhooks = signal(false);
    isSavingWebhook = signal(false);
    editingWebhookId = signal<number | null>(null);
    showAddWebhookForm = signal(false);
    hasPaymentWebhook = computed(() => this.webhooks().length > 0);
    paymentWebhook = computed(() => this.webhooks().length > 0 ? this.webhooks()[0] : null);
    hasFailedKyc: boolean;
    isKycPending: boolean;
    /**
     * Hides the operational config sections (invoice profile, payment webhook, e-commerce config, API credentials) for
     * e-commerce merchants still on the onboarding landing flow, until their KYC is verified.
     */
    hideOnboardingSections = false;
    natureOfBusinessList: BusinessNature[] = [];
    natureOfBusinessNotSelected = false;
    otherNatureOfBusinessSelected = false;
    readonly provinceList: DropdownItem[] = ConstantsUtil.PROVINCE_LIST.map(p => ({value: p}));
    readonly businessTypeList: DropdownItem[] = ConstantsUtil.BUSINESS_TYPE_LIST;
    showOAuthModal = false;
    oAuthCredentials: { clientId: string; clientSecret: string } | null = null;
    hasOAuthCredentials = false;
    copiedFields: { [key: string]: boolean } = {};
    private readonly WEBHOOK_TYPE = WebhookType.PAYMENT_NOTIFICATION;
    private readonly destroyRef = inject(DestroyRef);
    private maxLogoSize = 512000;
    private kycStatus: KycStatus;
    private natureOfBusinessOtherValue: string;
    private visibleSecrets: Set<number> = new Set();
    private visiblePublicKeys: Set<number> = new Set();
    private regeneratingStores: Set<number> = new Set();
    private keysDialogStoreId: number | null = null;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected webBusinessService: WebBusinessService,
        protected publicService: PublicService,
        protected layoutService: LayoutService,
        private ngxLoader: NgxUiLoaderService,
        private confirmationService: ConfirmationService,
        private pDialogService: DialogService,
        private activatedRoute: ActivatedRoute,
        private viewportScroller: ViewportScroller,
        private ecommerceVerificationService: EcommerceVerificationService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
    }

    /**
     * What to tell the merchant about a store's compliance check. Shared with the dashboard, which shows the same
     * badge against the selected store.
     *
     * @param {BusinessECommerceStore} businessECommerceStore - the store link to describe
     * @return {ECommerceStoreVerificationDisplay} the badge text, styling and tooltip for that store
     */
    getECommerceStoreVerification(businessECommerceStore: BusinessECommerceStore): ECommerceStoreVerificationDisplay
    {
        return resolveStoreVerification(businessECommerceStore);
    }

    /**
     * The store whose keys the keys dialog is showing, read from the store list so it reflects regenerated keys and
     * a disabled status as soon as they land.
     *
     * @return {BusinessECommerceStore | null} the store in the dialog, or null when the dialog is closed
     */
    get keysDialogStore(): BusinessECommerceStore | null
    {
        return this.businessECommerceStores?.find(store => store.id === this.keysDialogStoreId) ?? null;
    }

    get isECommerceStoreSelected(): boolean
    {
        return !!this.eCommerceStoreForm.controls.selectedECommerceStore.value;
    }

    get isECommerceStoreDomainValid(): boolean
    {
        const control = this.eCommerceStoreForm.controls.eCommerceStoreDomain;
        return control.valid && !!control.value?.trim();
    }

    /**
     * Store types the merchant may pick in the link dropdown. Every type stays selectable — a merchant may hold more
     * than one store of the same type, told apart by its storefront domain. Derived (not mutated) so it stays correct
     * after re-loads.
     */
    get selectableECommerceStores(): ECommerceStore[]
    {
        return this.availableECommerceStores ?? [];
    }

    get ficaDetails()
    {
        return (<UntypedFormGroup> this.form.get('ficaDetails')).controls;
    }

    get address()
    {
        return (<UntypedFormGroup> this.form.get(['businessDetails', 'address'])).controls;
    }

    get businessDetails()
    {
        return (<UntypedFormGroup> this.form.get('businessDetails')).controls;
    }

    get bankDetails()
    {
        return (<UntypedFormGroup> this.form.get('bankDetails')).controls;
    }

    get isSpecialMerchant(): boolean
    {
        return this.businessDetailsDTO?.specialMerchant || null;
    }

    get webhookUrlControl(): FormControl<string>
    {
        return this.webhookForm.controls.url;
    }

    ngOnInit()
    {
        this.canManageECommerceStoreCredentials =
            this.authenticationService.hasPrivilege('ECOMMERCE_STORE_MANAGE');

        this.webhookForm = new FormGroup({
            url: new FormControl('', [Validators.required, SyncValidators.validateUrl()])
        });

        this.eCommerceStoreForm = new FormGroup({
            selectedECommerceStore: new FormControl<ECommerceStore | null>(null),
            eCommerceStoreDomain: new FormControl<string>('', [SyncValidators.validateDomain()]),
            eCommerceStoreDescription: new FormControl<string>('', Validators.maxLength(255))
        });
        this.requireDomainOnceStoreSelected();

        this.form = this.formBuilder.group({
            businessDetails: this.formBuilder.group({
                contactNumber: ['', [Validators.required, SyncValidators.validateMobileNumber()]],
                displayName: ['', [Validators.required, Validators.maxLength(30)]],
                contactEmail: ['', [Validators.required, Validators.email]],
                logo: [''],
                useVatInclusiveAmount: [{value: 'false', disabled: true}, Validators.required],
                disclaimer: ['', Validators.maxLength(1000)],
                sendInvoice: ['true'],
                customRefEnabled: [],
                bulkEnabled: [],
                qrCodesEnabled: [],
                multiPremiumEnabled: [false],
                eCommerceMerchant: [{value: false, disabled: false}],
                address: this.formBuilder.group({
                    city: [{value: '', disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)}],
                    address1: [
                        {
                            value: '',
                            disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                        }
                    ],
                    address2: [
                        {
                            value: '',
                            disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                        }
                    ],
                    postalCode: [
                        {
                            value: '',
                            disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                        }
                    ],
                    province: [
                        {
                            value: '',
                            disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                        }
                    ]
                }),
                specialMerchant: [],
                billingEmail: ['', [Validators.required, Validators.email]],
                merchantPrefix: null
            }),
            ficaDetails: this.formBuilder.group({
                businessName: [
                    {
                        value: '',
                        disabled: true
                    }, [Validators.required, Validators.maxLength(255)], this.validateBusinessName.bind(this)
                ],
                businessType: [
                    {
                        value: '',
                        disabled: true
                    }, Validators.required
                ],
                natureOfBusiness: [
                    {
                        value: '',
                        disabled: true
                    }, Validators.required
                ],
                natureOfBusinessOther: [
                    {
                        value: '',
                        disabled: true
                    }, [Validators.required, Validators.maxLength(400)]
                ],
                fspNumber: ['', SyncValidators.validateFSPNumber()],
                registrationNumber: [
                    {
                        value: '',
                        disabled: true
                    },
                    [
                        Validators.required, SyncValidators.validateBusinessRegistrationNumber(
                        () => this.form?.get('ficaDetails.businessType')?.value)
                    ],
                    this.validateBusinessRegistrationNumber.bind(this)
                ],
                vatNumber: [
                    {
                        value: '',
                        disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                    }, of(null), this.validateVATNumber.bind(this)
                ],
                taxNumber: [
                    {
                        value: '',
                        disabled: !this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS)
                    }, of(null), this.validateTAXNumber.bind(this)
                ],
                directorName: [
                    {
                        value: '',
                        disabled: true
                    }, Validators.required
                ],
                directorSurname: [
                    {
                        value: '',
                        disabled: true
                    }, Validators.required
                ],
                directorIdNumber: [
                    {
                        value: '',
                        disabled: true
                    }, [Validators.required, SyncValidators.validateSAIdNumber]
                ],
                overview: [
                    {
                        value: '',
                        disabled: true
                    }, [Validators.required, Validators.maxLength(250)]
                ],
                websiteUrl: [{value: '', disabled: true}, [Validators.maxLength(100)]],
                kycStatus: [{value: KycStatus.PENDING, disabled: true}]
            }),
            bankDetails: this.formBuilder.group({
                bankName: [
                    {
                        value: '',
                        disabled: (!this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_BANKING_DETAILS))
                    }, Validators.required
                ],
                bankBranch: [
                    {
                        value: '',
                        disabled: (!this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_BANKING_DETAILS))
                    }, [Validators.required, Validators.maxLength(10), Validators.pattern('^[0-9]*$')],
                ],
                bankAccountNumber: [
                    {
                        value: '',
                        disabled: (!this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_BANKING_DETAILS))
                    }, [Validators.required, Validators.maxLength(30), Validators.pattern('^[0-9]*$')],
                ]
            }),
            marketingConsent: [false]
        });

        this.loadFormDetails();
    }

    loadFormDetails()
    {
        // bring up the loading screen
        this.ngxLoader.start();

        // observable used to load banking details for the current business
        const loadBankingDetails$ = this.webBusinessService.getBankingDetails().pipe(switchMap((bankDetails) =>
        {
            this.bankDetailsDTO = bankDetails;
            this.setBankDetails(bankDetails);
            return of(true);
        }));

        // observable used to load nature of business list followed by Fica details
        const loadNatureOfBusinessListAndFicaDetails$ = this.publicService.getNatureOfBusinessList().pipe(switchMap(
            (natureOfBusinessList) =>
            {
                natureOfBusinessList.push({name: 'Other'});
                this.natureOfBusinessList = natureOfBusinessList;
                return loadFicaDetails$;
            }));

        // observable used to load FICA details for the current business
        // do not load Fica details directly, rather use the {@loadNatureOfBusinessListAndFicaDetails} observable.
        const loadFicaDetails$ = this.webBusinessService.getFicaDetails().pipe(switchMap((ficaDetails) =>
        {
            this.ficaDetailsDTO = ficaDetails;
            this.isSoleProprietor = this.ficaDetailsDTO.businessType.valueOf() === BusinessType.SOLE_PROPRIETOR.valueOf();
            this.setFicaDetails(ficaDetails);
            return of(true);
        }));

        // observable used to load non-specific details relevant to the current business
        const loadBusinessDetails$ = this.webBusinessService.getBusinessDetails().pipe(switchMap((businessDetails) =>
        {
            this.businessDetailsDTO = businessDetails;
            if (businessDetails.logo)
            {
                this.logoImage = 'data:image/png;base64,' + businessDetails.logo;
            }
            this.setBusinessDetails(businessDetails);
            return of(true);
        }));

        // observable used to load a list of banks and their details
        const loadBanks$ = this.publicService.getBanks().pipe(switchMap((banks) =>
        {
            this.banks = banks;
            return of(true);
        }));

        const loadECommerceStores$ = this.webBusinessService.getECommerceStoreList().pipe(switchMap((eCommerceStores) =>
        {
            this.availableECommerceStores = eCommerceStores;
            return of(true);
        }));


        // fork joining allows the different account setting to be loaded asynchronously while letting us know when
        // all the loads have completed, so we can kill the loading screen and allow user interaction.
        const observables = [
            loadNatureOfBusinessListAndFicaDetails$,
            loadBusinessDetails$,
            loadBankingDetails$,
            loadBanks$,
            loadECommerceStores$
        ];

        forkJoin(observables).pipe(finalize(() => this.ngxLoader.stop())).subscribe({
            next: () =>
            {
                this.checkIfOAuthCredentialsExist();
                this.loadECommerceStoresForBusiness();
                this.loadWebhooks();
                const fragment = this.activatedRoute.snapshot.fragment;
                if (fragment)
                {
                    this.viewportScroller.scrollToAnchor(fragment);
                }
            },
            error: (error) =>
            {
                // stop the loading screen if there is an error
                this.alertService.error('An error occurred while attempting to load account settings. Please try' +
                    ' again later.', true);
                this.debugOutput('error', error);
                this.router.navigate([this.routes.SECURE.HOME.VIEW.path]).then();
            }
        });

        this.f.ficaDetails.get('natureOfBusiness').valueChanges.subscribe(natureOfBusiness =>
        {
            this.natureOfBusinessNotSelected = false;
            if (natureOfBusiness === 'Other')
            {
                this.otherNatureOfBusinessSelected = true;
                this.f.ficaDetails.get('natureOfBusinessOther').enable();
            }
            else
            {
                this.otherNatureOfBusinessSelected = false;
                this.f.ficaDetails.get('natureOfBusinessOther').disable();
            }

            this.updateFSPNumberField(natureOfBusiness);
        });
    }

    updateFSPNumberField(natureOfBusiness: string)
    {
        if (natureOfBusiness == 'Insurance')
        {
            this.f.ficaDetails.get('fspNumber').addValidators([Validators.required]);
            this.f.ficaDetails.get('fspNumber').updateValueAndValidity();
        }
        else
        {
            this.f.ficaDetails.get('fspNumber').removeValidators([Validators.required]);
            this.f.ficaDetails.get('fspNumber').updateValueAndValidity();
        }
    }

    /** Makes the storefront domain required only while an e-commerce store is selected. */
    private requireDomainOnceStoreSelected(): void
    {
        requireWhileSelected(
            this.eCommerceStoreForm.controls.selectedECommerceStore,
            this.eCommerceStoreForm.controls.eCommerceStoreDomain,
            takeUntilDestroyed(this.destroyRef));
    }

    refreshIsSoleProprietor()
    {
        const businessType: string = this.f.ficaDetails.get('businessType').value;
        this.isSoleProprietor = businessType === BusinessType.SOLE_PROPRIETOR.valueOf();
        if (this.isSoleProprietor)
        {
            this.f.ficaDetails.get('registrationNumber').disable();
        }
        else
        {
            this.f.ficaDetails.get('registrationNumber').enable();
        }
    }

    setBusinessDetails(businessDetails: BusinessDetails)
    {
        this.f.businessDetails.patchValue(businessDetails);
        this.form.get('marketingConsent').setValue(businessDetails?.marketingConsent ?? false);
    }

    setKycStatus(ficaDetails: FicaDetails)
    {
        const kycStatus = ficaDetails?.kycStatus;
        this.hasFailedKyc = kycStatus === KycStatus.FAILED;
        this.isKycPending = kycStatus === KycStatus.PENDING;
        this.kycStatus = kycStatus;
        this.hideOnboardingSections =
            this.authenticationService.getUserLogin()?.eCommerceMerchant === true
            && this.ecommerceVerificationService.shouldDisplayLandingPage(
                {kycStatus: this.kycStatus}, this.isGuestAdminUser());
        if (this.hasFailedKyc)
        {
            this.enableFicaInfoControls();
            this.disableBankDetails();
            this.disableBusinessDetails();
        }
        else
        {
            if (this.isKycPending)
            {
                this.disableFicaInfoControls();
            }
            else
            {
                this.enableFicaInfoControls();
            }
            this.enableBusinessDetails();
        }
    }

    disableFicaInfoControls()
    {
        this.f.ficaDetails.get('businessName').disable();
        this.f.ficaDetails.get('businessType').disable();
        this.f.ficaDetails.get('registrationNumber').disable();
        this.f.ficaDetails.get('vatNumber').disable();
        this.f.ficaDetails.get('taxNumber').disable();
        this.f.ficaDetails.get('directorName').disable();
        this.f.ficaDetails.get('directorSurname').disable();
        this.f.ficaDetails.get('directorIdNumber').disable();
        this.f.ficaDetails.get('natureOfBusiness').disable();
        this.f.ficaDetails.get('natureOfBusinessOther').disable();
        this.f.ficaDetails.get('overview').disable();
        this.f.ficaDetails.get('websiteUrl').disable();
        this.f.ficaDetails.get('fspNumber').disable();
    }

    enableFicaInfoControls()
    {
        this.f.ficaDetails.get('businessName').enable();
        this.f.ficaDetails.get('businessType').enable();
        this.f.ficaDetails.get('vatNumber').enable();
        this.f.ficaDetails.get('taxNumber').enable();
        this.f.ficaDetails.get('directorName').enable();
        this.f.ficaDetails.get('directorSurname').enable();
        this.f.ficaDetails.get('directorIdNumber').enable();
        this.f.ficaDetails.get('natureOfBusiness').enable();
        this.f.ficaDetails.get('natureOfBusinessOther').enable();
        this.f.ficaDetails.get('overview').enable();
        this.f.ficaDetails.get('websiteUrl').enable();
        this.f.ficaDetails.get('fspNumber').enable();
        if (!this.isSoleProprietor)
        {
            this.f.ficaDetails.get('registrationNumber').enable();
            this.f.ficaDetails.get('registrationNumber').updateValueAndValidity();
        }
    }

    disableBankDetails()
    {
        this.f.bankDetails.get('bankName').disable();
        this.f.bankDetails.get('bankAccountNumber').disable();
        this.f.bankDetails.get('bankBranch').disable();
    }

    enableBankDetails()
    {
        this.f.bankDetails.get('bankName').enable();
        this.f.bankDetails.get('bankAccountNumber').enable();
        this.f.bankDetails.get('bankBranch').enable();
    }

    disableBusinessDetails()
    {
        this.f.businessDetails.disable();
    }

    enableBusinessDetails()
    {
        this.f.businessDetails.enable();
    }

    setFicaDetails(ficaDetails: FicaDetails)
    {
        this.natureOfBusinessOtherValue = '';
        if (ficaDetails.natureOfBusiness === null)
        {
            this.natureOfBusinessNotSelected = true;
        }
        else if (this.natureOfBusinessNotInList(ficaDetails.natureOfBusiness))
        {
            this.natureOfBusinessOtherValue = ficaDetails.natureOfBusiness;
            ficaDetails.natureOfBusiness = 'Other';
            this.otherNatureOfBusinessSelected = true;
        }
        const ficaDetailsForm = {...ficaDetails, natureOfBusinessOther: this.natureOfBusinessOtherValue};
        this.f.ficaDetails.setValue(ficaDetailsForm);
        this.setKycStatus(ficaDetails);
        if (ficaDetails.natureOfBusiness !== 'Other' || ficaDetails.kycStatus === KycStatus.PENDING)
        {
            this.f.ficaDetails.get('natureOfBusinessOther').disable();
        }

        this.updateFSPNumberField(ficaDetailsForm.natureOfBusiness);
    }

    natureOfBusinessNotInList(natureOfBusinessName: string): boolean
    {
        const natureOfBusinessValue = this.natureOfBusinessList.find((natureOfBusiness) =>
        {
            return natureOfBusiness.name === natureOfBusinessName;
        });
        return !natureOfBusinessValue;
    }

    setBankDetails(bankDetails: BankDetails)
    {
        if (bankDetails === null)
        {
            return;
        }

        if (bankDetails.bankName != null && bankDetails.bankName.length > 0)
        {
            this.f.bankDetails.get('bankName').setValue(bankDetails.bankName);
        }

        if (bankDetails.bankAccountNumber != null && bankDetails.bankAccountNumber.length > 0)
        {
            this.f.bankDetails.get('bankAccountNumber').setValue(bankDetails.bankAccountNumber);
        }

        if (bankDetails.bankBranch != null && bankDetails.bankBranch.length > 0)
        {
            this.f.bankDetails.get('bankBranch').setValue(bankDetails.bankBranch);
        }

        if (bankDetails.verificationStatus === VerificationStatus.PENDING)
        {
            this.disableBankDetails();
        }
        else
        {
            this.enableBankDetails();
        }
    }

    isInvoiceEnabled(): string
    {
        return (Boolean(this.businessDetails.sendInvoice.value)) ? 'YES' : 'NO';
    }

    isCustomRefEnabled(): string
    {
        return (Boolean(this.businessDetails.customRefEnabled.value)) ? 'YES' : 'NO';
    }

    useVatInclusiveAmountLabel(): string
    {
        return (Boolean(this.businessDetails.useVatInclusiveAmount.value)) ? 'YES' : 'NO';
    }

    multiPremiumEnabledLabel(): string
    {
        return (Boolean(this.businessDetails.multiPremiumEnabled.value)) ? 'YES' : 'NO';
    }

    marketingConsentLabel(): string
    {
        return (Boolean(this.form.get('marketingConsent').value)) ? 'YES' : 'NO';
    }

    isFicaDetailsVerified(): boolean
    {
        return (this.ficaDetailsDTO != null && VerificationStatus.VERIFIED === this.ficaDetailsDTO.kycStatus);
    }

    isBankDetailsVerified(): boolean
    {
        return (this.bankDetailsDTO != null && VerificationStatus.VERIFIED === this.bankDetailsDTO.verificationStatus);
    }

    isBankDetailsFailed(): boolean
    {
        return (this.bankDetailsDTO != null && VerificationStatus.FAILED === this.bankDetailsDTO.verificationStatus);
    }

    isBankDetailsPending(): boolean
    {
        return (this.bankDetailsDTO != null && VerificationStatus.PENDING === this.bankDetailsDTO.verificationStatus);
    }

    isBankDetailsEmpty(): boolean
    {
        return (this.bankDetailsDTO == null || this.bankDetailsDTO.verificationStatus == null);
    }

    isBankDetailsChanged(): boolean
    {
        const bankDetailsNotEmpty = !!this.bankDetails.bankName.value || !!this.bankDetails.bankBranch.value ||
            !!this.bankDetails.bankAccountNumber.value;

        if (this.bankDetailsDTO != null)
        {
            const bankDetailsDTONotNull = !!this.bankDetailsDTO.bankName ||
                !!this.bankDetailsDTO.bankBranch ||
                !!this.bankDetailsDTO.bankAccountNumber;

            const bankDetailsNotSame = this.bankDetailsDTO.bankName !== this.bankDetails.bankName.value ||
                this.bankDetailsDTO.bankBranch !== this.bankDetails.bankBranch.value ||
                this.bankDetailsDTO.bankAccountNumber !== this.bankDetails.bankAccountNumber.value;

            return (bankDetailsNotSame && (bankDetailsDTONotNull || bankDetailsNotEmpty));
        }
        return bankDetailsNotEmpty;
    }

    isBusinessDetailsChanged(): boolean
    {
        return (this.businessDetailsDTO != null && (
            this.businessDetailsDTO.contactNumber !== this.businessDetails.contactNumber.value ||
            this.businessDetailsDTO.displayName !== this.businessDetails.displayName.value ||
            this.businessDetailsDTO.contactEmail !== this.businessDetails.contactEmail.value ||
            this.businessDetailsDTO.logo !== this.businessDetails.logo.value ||
            this.businessDetailsDTO.disclaimer !== this.businessDetails.disclaimer.value ||
            this.businessDetailsDTO.sendInvoice !== this.businessDetails.sendInvoice.value ||
            this.businessDetailsDTO.customRefEnabled !== this.businessDetails.customRefEnabled.value ||
            this.businessDetailsDTO.address.city !== this.address.city.value ||
            this.businessDetailsDTO.address.address1 !== this.address.address1.value ||
            this.businessDetailsDTO.address.address2 !== this.address.address2.value ||
            this.businessDetailsDTO.address.postalCode !== this.address.postalCode.value ||
            this.businessDetailsDTO.address.province !== this.address.province.value ||
            this.businessDetailsDTO.billingEmail !== this.businessDetails.billingEmail.value ||
            this.businessDetailsDTO.useVatInclusiveAmount !== this.businessDetails.useVatInclusiveAmount.value ||
            this.businessDetailsDTO.multiPremiumEnabled !== this.businessDetails.multiPremiumEnabled.value
        ));
    }

    isMarketingConsentChanged(): boolean
    {
        return (this.businessDetailsDTO != null &&
            this.businessDetailsDTO.marketingConsent !== this.form.get('marketingConsent').value);
    }

    isFicaDetailsChanged(): boolean
    {
        return (this.ficaDetails != null && (
            this.ficaDetailsDTO.businessType !== this.ficaDetails.businessType.value ||
            this.hasNatureOfBusinessChanged() ||
            this.ficaDetailsDTO.businessName !== this.ficaDetails.businessName.value ||
            this.ficaDetailsDTO.directorIdNumber !== this.ficaDetails.directorIdNumber.value ||
            this.ficaDetailsDTO.directorName !== this.ficaDetails.directorName.value ||
            this.ficaDetailsDTO.directorSurname !== this.ficaDetails.directorSurname.value ||
            this.ficaDetailsDTO.registrationNumber !== this.ficaDetails.registrationNumber.value ||
            this.ficaDetailsDTO.taxNumber !== this.ficaDetails.taxNumber.value ||
            this.ficaDetailsDTO.vatNumber !== this.ficaDetails.vatNumber.value ||
            this.ficaDetailsDTO.websiteUrl !== this.ficaDetails.websiteUrl.value ||
            this.ficaDetailsDTO.fspNumber !== this.ficaDetails.fspNumber.value ||
            this.ficaDetailsDTO.overview !== this.ficaDetails.overview.value));
    }

    hasNatureOfBusinessChanged(): boolean
    {
        if (this.natureOfBusinessNotSelected)
        {
            return false;
        }
        else if (this.otherNatureOfBusinessSelected)
        {
            return this.natureOfBusinessOtherValue !== this.ficaDetails.natureOfBusinessOther.value;
        }
        else
        {
            return this.ficaDetailsDTO.natureOfBusiness !== this.ficaDetails.natureOfBusiness.value;
        }
    }

    getBankDetailsToolTip(): string
    {
        const defaultMessage = 'Your banking details have not yet been filled in. Please complete the bank details form.';

        if (this.bankDetailsDTO == null || this.bankDetailsDTO.verificationStatus == null)
        {
            return defaultMessage;
        }

        switch (this.bankDetailsDTO.verificationStatus)
        {
            case VerificationStatus.FAILED:
                return 'Your banking details failed the account verification service (AVS) check. Please ' +
                    'update your banking details with valid, up-to-date information.';
            case VerificationStatus.PENDING:
                return 'Your banking details have been submitted and are currently awaiting ' +
                    'the results of an account verification (AVS) check. You will not be able to update your banking ' +
                    'details again until this check has been completed.';
            case VerificationStatus.VERIFIED:
                return 'Your banking details have been successfully verified.';
            default:
                return defaultMessage;
        }
    }

    getKycStatusToolTip(): string
    {
        const defaultMessage = 'Your KYC verification has failed. Please update your business details and resubmit.';

        switch (this.kycStatus)
        {
            case KycStatus.FAILED:
                return defaultMessage;
            case KycStatus.PENDING:
                return 'Verification of your KYC compliance is pending. You will have limited ' +
                    '\n functionality until this has been completed';
            case KycStatus.VERIFIED:
                return 'Your KYC compliance has been successfully verified.';
            default:
                return defaultMessage;
        }
    }

    validateBusinessName(control: AbstractControl)
    {
        if (!control.value)
        {
            return of(null);
        }

        if (this.ficaDetailsDTO.businessName !== control.value)
        {
            return timer(1000).pipe(
                switchMap(() =>
                {
                    return this.webBusinessService.isBusinessExists(
                        null,
                        null,
                        control.value,
                        null)
                        .pipe(rxMap((res) =>
                        {
                            return res.responseCode === ResponseEnum.TRUE
                                ?
                                {duplicateBusinessName: true} : null;
                        }));
                })
            );
        }
        return of(null);
    }

    validateBusinessRegistrationNumber(control: AbstractControl)
    {
        if (!control.value)
        {
            return of(null);
        }

        if (this.ficaDetailsDTO.registrationNumber !== control.value)
        {
            return timer(1000).pipe(
                switchMap(() =>
                {
                    return this.webBusinessService.isBusinessExists(
                        null,
                        null,
                        null,
                        control.value)
                        .pipe(rxMap((res) =>
                        {
                            if (res.responseCode === ResponseEnum.TRUE)
                            {
                                return {backendValidation: res.responseMessage};
                            }
                            if (res.responseCode === ResponseEnum.FALSE || res.responseCode === ResponseEnum.ERROR)
                            {
                                return {customValidation: res.responseMessage};
                            }
                            return null;
                        }));
                })
            );
        }
        return of(null);
    }

    validateVATNumber(control: AbstractControl)
    {
        if (!control.value)
        {
            return of(null);
        }

        if (this.ficaDetailsDTO.vatNumber !== control.value)
        {
            return timer(1000).pipe(
                switchMap(() =>
                {
                    return this.webBusinessService.isBusinessExists(
                        control.value,
                        null,
                        null,
                        null)
                        .pipe(rxMap((res) =>
                        {
                            return res.responseCode === ResponseEnum.TRUE
                                ?
                                {duplicateVATNumber: true} : null;
                        }));
                })
            );
        }
        return of(null);
    }

    validateTAXNumber(control: AbstractControl)
    {
        if (!control.value)
        {
            return of(null);
        }

        if (this.ficaDetailsDTO.taxNumber !== control.value)
        {
            return timer(1000).pipe(
                switchMap(() =>
                {
                    return this.webBusinessService.isBusinessExists(
                        null,
                        control.value,
                        null,
                        null)
                        .pipe(rxMap((res) =>
                        {
                            return res.responseCode === ResponseEnum.TRUE
                                ?
                                {duplicateTAXNumber: true} : null;
                        }));
                })
            );
        }
        return of(null);
    }

    updateBank(): void
    {
        const bankName = this.bankDetails.bankName.value;

        this.debugOutput('Bank Name: ' + bankName);

        const bank = this.banks.find(bank => bank.bankName === bankName);


        this.debugOutput('Bank Branch: ' + bank.defaultBranchNumber);
        this.bankDetails.bankBranch.setValue(bank.defaultBranchNumber);
    }

    /**
     * This method will wait for the user to confirm that they wish to update their banking details before submitting
     * the account settings change upstream.
     */
    async updateAccountSettings()
    {
        if (this.isFicaDetailsChanged() && this.isFicaDetailsVerified())
        {
            const confirmed = <boolean> await this.confirmationService.confirm(
                'Change Business Details',
                'You will not be able to create request to pay transactions until your new business' +
                ' details have undergone a verification check (KYC). This may take 24-48' +
                ' hours to complete.');

            if (!confirmed)
            {
                this.loading = false;
                return;
            }
        }

        if (this.isBankDetailsChanged() && this.isBankDetailsVerified())
        {
            const confirmed = <boolean> await this.confirmationService.confirm(
                'Change Banking Details',
                'You will not be able to create request to pay transactions until your new banking' +
                ' details have undergone an account verification (AVS) check. This may take 24-48' +
                ' hours to complete.');

            if (!confirmed)
            {
                this.loading = false;
                return;
            }
        }

        this.ngxLoader.start();
        this.updateFormInfo().subscribe({
            next: () =>
            {
                this.logoFile = null;
                // tell the layout service that items on the menu-bar/sidebar need to be
                // updated following an update.
                this.layoutService.loadBusinessDetails();
                this.ngxLoader.stop();
                this.alertService.success('Changes have been saved.');

                // reload all form components
                this.ngOnInit();
            },
            error: (error) =>
            {
                this.ngxLoader.stop();
                this.debugOutput(error);

                let errorMessage = 'An error occurred while attempting to update account details. ' +
                    'Please try again later.';

                if (error.status === 422)
                {
                    errorMessage = error.error.errorMessage;
                }
                this.alertService.error(errorMessage);
            }
        });

        this.loading = false;
    }

    submit()
    {
        this.submitted = true;
        this.loading = true;

        if (!(this.form.status === 'VALID'))
        {
            this.loading = false;
            this.alertService.error('Some form values are missing or invalid. Please ensure all required fields ' +
                'have been filled in with valid values.');
            return;
        }

        if (!this.isBusinessDetailsChanged() && !this.isFicaDetailsChanged() && !this.isBankDetailsChanged()
            && !this.isMarketingConsentChanged() && !this.logoFile)
        {
            this.loading = false;
            this.alertService.error('No changes have been made');
            return;
        }

        this.updateAccountSettings().catch(() =>
        {
            this.alertService.error('An error occurred while attempting to update ' +
                'account settings. Please try again later.');
        });
    }

    /**
     * Retrieves the updated business details based on the current form values.
     *
     * @return {BusinessDetails} An object containing the updated business details, such as display name,
     * contact information, address, preferences, and other business-related settings.
     */
    getUpdatedBusinessDetails(): BusinessDetails
    {
        const businessDetails = this.f.businessDetails.value;
        return {
            displayName: businessDetails?.displayName,
            contactEmail: businessDetails?.contactEmail,
            contactNumber: businessDetails?.contactNumber,
            disclaimer: businessDetails?.disclaimer,
            logo: businessDetails?.logo,
            sendInvoice: businessDetails?.sendInvoice,
            customRefEnabled: businessDetails?.customRefEnabled,
            bulkEnabled: businessDetails?.bulkEnabled,
            specialMerchant: businessDetails?.specialMerchant,
            address: businessDetails?.address,
            billingEmail: businessDetails?.billingEmail,
            merchantPrefix: businessDetails?.merchantPrefix,
            qrCodesEnabled: businessDetails?.qrCodesEnabled,
            merchantSelfServiceEnabled: businessDetails?.merchantSelfServiceEnabled,
            useVatInclusiveAmount: businessDetails?.useVatInclusiveAmount,
            vatRegistered: businessDetails?.vatRegistered,
            multiPremiumEnabled: businessDetails?.multiPremiumEnabled,
            eCommerceMerchant: businessDetails?.eCommerceMerchant,
            marketingConsent: this.form.get('marketingConsent')?.value,
        } as BusinessDetails;
    }

    updateFormInfo(): Observable<any>
    {
        let businessObservable: Observable<any> = this.publicService.ping();

        if (this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT)
            && (this.f.businessDetails.dirty || this.isMarketingConsentChanged()))
        {
            businessObservable =
                businessObservable.pipe(switchMap(() =>
                    this.webBusinessService.updateBusinessDetails(this.getUpdatedBusinessDetails())));

            if (this.logoFile && this.businessDetails.logo.dirty)
            {
                businessObservable =
                    businessObservable.pipe(switchMap(() => this.webBusinessService.uploadLogo(this.logoFile)));
            }
        }

        if (this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_FICA_DETAILS) && this.f.ficaDetails.dirty)
        {
            const ficaDetailsFormValue = {
                ...this.getUpdatedFicaForm(),
                registrationNumber: this.isSoleProprietor ? null : this.f.ficaDetails.get('registrationNumber').value
            };

            businessObservable =
                businessObservable.pipe(switchMap(
                    () => this.webBusinessService.updateFicaDetails(ficaDetailsFormValue)
                ));
        }

        // Only update banking details only if the following conditions are met:
        // 1. The user has the correct permissions
        // 2. The bank details have actually changed
        // 3. The current bank details are not pending an existing AVS check
        if (this.hasRouteAccess(this.routes.SECURE.BUSINESS.EDIT_BANKING_DETAILS) && this.isBankDetailsChanged() &&
            !this.isBankDetailsPending())
        {
            businessObservable = businessObservable.pipe(switchMap(() => this.webBusinessService.updateBankingDetails(
                this.f.bankDetails.value)));
        }

        return businessObservable;
    }

    loadAndValidateLogo(event: Event)
    {
        const target = event.target as HTMLInputElement;

        if (target.files[0])
        {
            const file = target.files?.[0];
            this.debugOutput(`file size is ${file.size} and file type is ${file.type}`);

            if (!this.isExpectedImageType(file))
            {
                this.alertService.error('Incorrect file type received. Only .png or .jpg images are supported');
                return;
            }

            if (!this.isExpectedImageSize(file))
            {
                this.alertService.error('File received is too large. File size must be less than or equal to ' + (this.maxLogoSize / 1000) +
                    ' kb');
                return;
            }
            // mark the logo formControl as dirty which allows us to detect whether new input has been supplied for
            // on a form control and thus whether to update it.
            this.businessDetails.logo.markAsDirty();
            // load the logo image from the logo file
            this.logoFile = file;
            const reader = new FileReader();
            reader.readAsDataURL(this.logoFile); // read file as data url
            reader.onload = (readerLoad: any) =>
            { // called once readAsDataURL is completed
                this.logoImage = readerLoad.target.result;
            };
        }
    }

    getLogoFileName()
    {
        if (!this.logoFile)
        {
            return 'Browse for image...';
        }
        return this.logoFile.name;
    }

    testWebhookUrl(webhookUrl: string)
    {
        if (this.isSavingWebhook() || !webhookUrl)
        {
            return;
        }
        this.ngxLoader.start();
        this.webBusinessService.testWebhookUrl(webhookUrl)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.ngxLoader.stop())
            )
            .subscribe({
                    next: (responseDTO) =>
                    {
                        if (responseDTO.responseCode === ResponseEnum.FOUND)
                        {
                            this.alertService.success(responseDTO.responseMessage);
                        }
                        else if (responseDTO.responseCode === ResponseEnum.NOT_FOUND)
                        {
                            this.alertService.error(responseDTO.responseMessage);
                        }
                    },
                    error: (error) => this.handleApiError(
                        error,
                        'An error occurred while attempting to test the webhook URL. Please try again later.'
                    )
                }
            );
    }

    loadWebhooks()
    {
        this.isLoadingWebhooks.set(true);
        this.webBusinessService.getWebhooks(this.WEBHOOK_TYPE)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isLoadingWebhooks.set(false))
            )
            .subscribe({
                next: (webhooks: Webhook[]) =>
                {
                    this.webhooks.set(webhooks || []);
                },
                error: () =>
                {
                    this.alertService.error('Failed to load webhooks. Please try again later.');
                    this.webhooks.set([]);
                }
            });
    }

    showAddWebhook()
    {
        this.showAddWebhookForm.set(true);
        this.webhookForm.reset();
    }

    cancelAddWebhook()
    {
        this.showAddWebhookForm.set(false);
        this.webhookForm.reset();
    }

    createWebhook()
    {
        if (this.isSavingWebhook())
        {
            return;
        }

        if (this.webhookForm.invalid)
        {
            this.webhookForm.markAllAsTouched();
            return;
        }

        this.isSavingWebhook.set(true);
        const webhookCreate: WebhookCreate = {
            webhookUrl: this.webhookUrlControl.value.trim(),
            webhookType: this.WEBHOOK_TYPE,
            ownerType: 'BUSINESS',
            ownerId: this.authenticationService.getUserLogin().businessId
        };

        this.webBusinessService.createWebhook(webhookCreate)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isSavingWebhook.set(false))
            )
            .subscribe({
                next: () =>
                {
                    this.alertService.success('Webhook created successfully.');
                    this.showAddWebhookForm.set(false);
                    this.webhookForm.reset();
                    this.loadWebhooks();
                },
                error: (error) => this.handleApiError(error, 'Failed to create webhook. Please try again.')
            });
    }

    startEditWebhook(webhook: Webhook | null)
    {
        if (this.isSavingWebhook() || !webhook)
        {
            return;
        }
        this.editingWebhookId.set(webhook.id);
        this.webhookForm.patchValue({url: webhook.webhookUrl || ''});
    }

    cancelEditWebhook()
    {
        this.editingWebhookId.set(null);
        this.webhookForm.reset();
    }

    updateWebhook(webhook: Webhook | null)
    {
        if (this.isSavingWebhook() || !webhook)
        {
            return;
        }
        if (this.webhookForm.invalid)
        {
            this.webhookForm.markAllAsTouched();
            return;
        }

        this.isSavingWebhook.set(true);
        const webhookUpdate: WebhookUpdate = {
            webhookUrl: this.webhookUrlControl.value.trim()
        };

        this.webBusinessService.updateWebhook(webhook.id, webhookUpdate)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.isSavingWebhook.set(false))
            )
            .subscribe({
                next: () =>
                {
                    this.alertService.success('Webhook updated successfully.');
                    this.editingWebhookId.set(null);
                    this.webhookForm.reset();
                    this.loadWebhooks();
                },
                error: (error) => this.handleApiError(error, 'Failed to update webhook. Please try again.')
            });
    }

    deleteWebhook(webhook: Webhook | null)
    {
        if (this.isSavingWebhook() || !webhook)
        {
            return;
        }
        this.confirmationService.confirm(
            'Delete Webhook',
            `Are you sure you want to delete this webhook? This action cannot be undone.`)
            .then(confirmed =>
            {
                if (confirmed)
                {
                    this.isSavingWebhook.set(true);
                    this.webBusinessService.deleteWebhook(webhook.id)
                        .pipe(
                            takeUntilDestroyed(this.destroyRef),
                            finalize(() => this.isSavingWebhook.set(false))
                        )
                        .subscribe({
                            next: () =>
                            {
                                this.alertService.success('Webhook deleted successfully.');
                                this.loadWebhooks();
                            },
                            error: (error) => this.handleApiError(
                                error,
                                'Failed to delete webhook. Please try again.')
                        });
                }
            });
    }

    isEditingWebhook(webhook: Webhook | null): boolean
    {
        return webhook !== null && this.editingWebhookId() === webhook.id;
    }

    isExpectedImageType(file: File)
    {
        return file.type === 'image/png' || file.type === 'image/jpeg';
    }

    isExpectedImageSize(file: File)
    {
        return file.size <= this.maxLogoSize;
    }

    isLoading(): boolean
    {
        return this.loading;
    }

    async confirmCustomRefEnabledChange()
    {
        const confirmed = <boolean> await this.businessDetails.customRefEnabled.value
            ? await this.confirmationService.confirm(
                'Enabling Custom Account/Reference Number',
                'Selecting custom account references implies that a user-supplied account or reference number is required with every request to pay transaction. This will include bulk uploads and you will need to ensure every record must have an account or reference number.')
            : await this.confirmationService.confirm(
                'Disabling Custom Account/Reference Number',
                'Selecting auto-generate account references implies that the system will generate a 9-digit reference for every request to pay transaction. Note that any custom account or reference numbers entered during bulk upload will be ignored.');

        if (!confirmed)
        {
            this.businessDetails.customRefEnabled.setValue(!this.businessDetails.customRefEnabled.value);
            this.loading = false;
            return;
        }
    }

    generateOAuthCredentials()
    {
        this.ngxLoader.start();
        this.webBusinessService.createMerchantOAuthClient()
            .pipe(finalize(() => this.ngxLoader.stop()))
            .subscribe({
                next: (response) =>
                {
                    this.oAuthCredentials = response;
                    this.showOAuthModal = true;
                    this.hasOAuthCredentials = true;
                },
                error: (error) =>
                {
                    // Check if this is a duplicate credentials error
                    if (error.status === 409 || error.error?.errorMessage?.includes('OAuth credentials already exist'))
                    {
                        this.hasOAuthCredentials = true;
                        this.alertService.error('OAuth credentials already exist for this business.');
                    }
                    else
                    {
                        this.debugOutput('OAuth generation error', error);
                        this.alertService.error('Failed to generate OAuth credentials. Please try again.');
                    }
                }
            });
    }

    checkIfOAuthCredentialsExist()
    {
        this.webBusinessService.hasMerchantOAuthClient().subscribe({
            next: (hasCredentials) =>
            {
                this.hasOAuthCredentials = hasCredentials;
            },
            error: (error) =>
            {
                this.debugOutput('OAuth credential check error', error);
                this.hasOAuthCredentials = false;
                this.alertService.error('Failed to check OAuth credentials status. Please try again later.');
            }
        });
    }

    /**
     * Loads the e-commerce store list for the associated business if the business has e-commerce merchant enabled.
     */
    loadECommerceStoresForBusiness()
    {
        if (this.businessDetailsDTO?.eCommerceMerchant)
        {
            this.webBusinessService.getECommerceStoresForBusiness().subscribe({
                next: (businessECommerceStores: BusinessECommerceStore[]) =>
                {
                    this.businessECommerceStores = businessECommerceStores;
                }, error: () =>
                {
                    this.alertService.error('Failed to load e-commerce stores for the business.');
                }
            });
        }
    }

    /**
     * Copies the given string value to the clipboard.
     *
     * @param {string} value - the string value to be copied to the clipboard
     * @param {string} field - the field from which the value if to be copied from
     */
    copyToClipboard(value: string, field: string): void
    {
        const copyFailed = () =>
            this.alertService.error('Could not copy to the clipboard. Please copy the value manually.');
        writeToClipboard(value).then(() =>
        {
            this.copiedFields[field] = true;
            setTimeout(() =>
            {
                this.copiedFields[field] = false;
            }, 6000);
        }).catch(copyFailed);
    }

    /**
     * Toggles the visibility of the secret key for a specific e-commerce store.
     *
     * @param {number} storeId - the ID of the business e-commerce store
     */
    toggleSecretVisibility(storeId: number): void
    {
        if (this.visibleSecrets.has(storeId))
        {
            this.visibleSecrets.delete(storeId);
        }
        else
        {
            this.visibleSecrets.add(storeId);
        }
    }

    /**
     * Checks if the secret key is visible for a specific e-commerce store.
     *
     * @param {number} storeId - the ID of the business e-commerce store
     * @returns {boolean} true if the secret is visible, false otherwise
     */
    isSecretVisible(storeId: number): boolean
    {
        return this.visibleSecrets.has(storeId);
    }

    /**
     * Toggles the visibility of the public key for a specific e-commerce store.
     *
     * @param {number} storeId - the ID of the business e-commerce store
     */
    togglePublicKeyVisibility(storeId: number): void
    {
        if (this.visiblePublicKeys.has(storeId))
        {
            this.visiblePublicKeys.delete(storeId);
        }
        else
        {
            this.visiblePublicKeys.add(storeId);
        }
    }

    /**
     * Checks if the public key is currently visible for a specific e-commerce store.
     *
     * @param {number} storeId - the ID of the business e-commerce store
     * @returns {boolean} true if the public key is visible, false otherwise
     */
    isPublicKeyVisible(storeId: number): boolean
    {
        return this.visiblePublicKeys.has(storeId);
    }

    /**
     * Opens the keys dialog for a store.
     *
     * @param {BusinessECommerceStore} businessECommerceStore - the store whose keys to show
     */
    openStoreKeys(businessECommerceStore: BusinessECommerceStore): void
    {
        this.keysDialogStoreId = businessECommerceStore?.id ?? null;
    }

    /**
     * Closes the keys dialog, masking that store's keys again so they are hidden the next time it opens.
     */
    closeStoreKeys(): void
    {
        if (this.keysDialogStoreId !== null)
        {
            this.visibleSecrets.delete(this.keysDialogStoreId);
            this.visiblePublicKeys.delete(this.keysDialogStoreId);
        }
        this.keysDialogStoreId = null;
    }

    /**
     * Checks if keys are currently being regenerated for a specific e-commerce store.
     *
     * @param {number} storeId - the ID of the business e-commerce store
     * @returns {boolean} true if keys are being regenerated, false otherwise
     */
    isRegenerating(storeId: number): boolean
    {
        return this.regeneratingStores.has(storeId);
    }

    /**
     * Regenerates the keys for a specific e-commerce store by calling Helios. A disabled store is enabled again with
     * the new keys, which needs its storefront domain, so for one without a recorded domain the merchant is told to add
     * the store again instead.
     *
     * @param {BusinessECommerceStore} businessECommerceStore - the business e-commerce store for which to regenerate
     *     keys
     */
    regenerateECommerceStoreKeys(businessECommerceStore: BusinessECommerceStore): void
    {
        if (!businessECommerceStore?.id)
        {
            this.alertService.error('Invalid e-commerce store selected.');
            return;
        }

        const storeName = businessECommerceStore.eCommerceStoreName;
        const domain = businessECommerceStore.domain?.trim();
        const isEnabling = !businessECommerceStore.active;

        if (isEnabling && !domain)
        {
            this.alertService.error(`'${storeName}' has no storefront domain recorded, so its keys can't be regenerated here. Add the store again with its domain to enable it.`);
            return;
        }

        this.confirmationService.confirm(
            `Regenerate Keys for ${storeName}`,
            isEnabling
                ? `'${storeName}' at ${domain} is disabled. Regenerating its keys will enable it again. Your old keys will not work again, so update your plugin with the new ones.`
                : `Are you sure you want to regenerate the keys for '${storeName}'? This will invalidate the existing keys and generate new ones.`)
            .then(confirmed =>
                {
                    if (confirmed)
                    {
                        this.regeneratingStores.add(businessECommerceStore.id);
                        this.loading = true;

                        this.webBusinessService.regenerateECommerceStoreKeys(businessECommerceStore.id)
                            .pipe(finalize(() =>
                            {
                                this.loading = false;
                                this.regeneratingStores.delete(businessECommerceStore.id);
                                // Hide the secret after regeneration
                                this.visibleSecrets.delete(businessECommerceStore.id);
                            }))
                            .subscribe({
                                next: (updatedStore: BusinessECommerceStore) =>
                                {
                                    this.alertService.success(isEnabling
                                        ? `Successfully regenerated keys for ${storeName}. The store is enabled again, so copy the new keys into your plugin.`
                                        : `Successfully regenerated keys for ${storeName}.`);
                                    // Update the store in the list
                                    const index = this.businessECommerceStores.findIndex(s => s.id === businessECommerceStore.id);
                                    if (index !== -1)
                                    {
                                        this.businessECommerceStores[index] = updatedStore;
                                    }
                                },
                                error: (error: any) => this.handleApiError(
                                    error,
                                    'An unexpected error occurred while regenerating keys. Please try again. If the issue persists, contact support'
                                )
                            });
                    }
                }
            );
    }

    /**
     * Displays a confirmation message for disabling the provided e-commerce store for the business. If confirmed,
     * proceeds to disable it.
     *
     * @param {BusinessECommerceStore} businessECommerceStore - the business e-commerce store to be disabled
     */
    protected onDisableBusinessECommerceStore(businessECommerceStore: BusinessECommerceStore): void
    {
        if (businessECommerceStore?.active)
        {
            this.confirmationService.confirm(
                `Disable ${businessECommerceStore?.eCommerceStoreName} store`,
                `Are you sure you want to Disable '${businessECommerceStore?.eCommerceStoreName}'  e-commerce store?`)
                .then(confirmed =>
                    {
                        if (confirmed)
                        {
                            this.closeStoreKeys();
                            this.disableBusinessECommerceStore(businessECommerceStore);
                        }
                    }
                );
        }
    }

    protected openResetOAuthClientSecretDialog(): void
    {
        this.pDialogService.open(
            ResetMerchantClientSecretComponent,
            {
                header: 'Reset OAuth Client Secret',
                closable: true,
                modal: true,
                styleClass: 'form-dialog'
            })?.onClose
            .pipe(take(1))
            .subscribe(
                (email: string) =>
                {
                    if (!email)
                    {
                        return;
                    }
                    const merchantOauthClientSecretResetRequest: MerchantOauthClientSecretResetRequest = {
                        businessId: this.authenticationService.getUserLogin().businessId,
                        resetEmail: email.trim()
                    };

                    this.resetMerchantOAuthClientSecret(merchantOauthClientSecretResetRequest);
                }
            );
    }

    /**
     * Adds a selected e-commerce store to the merchant if valid conditions are met.
     */
    protected addECommerceStoreToMerchant()
    {
        const selectedECommerceStore = this.eCommerceStoreForm.controls.selectedECommerceStore.value;
        const domain = this.eCommerceStoreForm.controls.eCommerceStoreDomain.value?.trim();
        const description = this.eCommerceStoreForm.controls.eCommerceStoreDescription.value?.trim() || undefined;

        if (!!selectedECommerceStore && !!this.businessDetailsDTO?.eCommerceMerchant)
        {
            // Client-side duplicate guard (only meaningful when domain is captured).
            if (domain && this.isECommerceStoreAlreadyLinked(selectedECommerceStore.id, domain))
            {
                this.alertService.info(`${selectedECommerceStore.storeName} at ${domain} is already linked to your business.`);
                return;
            }

            const confirmDetail = domain
                ? `Are you sure you want to add ${selectedECommerceStore?.storeName} at ${domain} as one of your e-commerce stores?`
                : `Are you sure you want to add ${selectedECommerceStore?.storeName} as one of your e-commerce stores?`;

            this.confirmationService.confirm(`Add ${selectedECommerceStore?.storeName} store`, confirmDetail)
                .then(confirmed =>
                    {
                        if (confirmed)
                        {
                            const businessECommerceStoreInput: BusinessECommerceStoreInput = {
                                eCommerceStoreId: selectedECommerceStore?.id,
                                eCommerceStoreName: selectedECommerceStore?.storeName,
                                domain: domain,
                                description: description
                            };

                            this.loading = true;

                            this.webBusinessService.addECommerceStoreToBusiness(businessECommerceStoreInput).pipe(finalize(() => this.loading = false)).subscribe(
                                {
                                    next: () =>
                                    {
                                        this.alertService.success(domain
                                            ? `Successfully added ${selectedECommerceStore?.storeName} e-commerce store at ${domain} to your business.`
                                            : `Successfully added ${selectedECommerceStore?.storeName} e-commerce store to your business.`);

                                        this.loadECommerceStoresForBusiness();
                                        this.eCommerceStoreForm.controls.selectedECommerceStore.reset();
                                        this.eCommerceStoreForm.controls.eCommerceStoreDomain.reset('');
                                        this.eCommerceStoreForm.controls.eCommerceStoreDescription.reset('');
                                    },
                                    error: (error: any) => this.handleApiError(
                                        error,
                                        'An unexpected error occurred. Please try again. If the issue persists, contact support.'
                                    )
                                });
                        }
                    }
                );
        }
    }

    /**
     * True if the business already has a linked store with the same store type and (normalised) domain. Best-effort
     * client guard against a redundant add — the server stays the source of truth via the unique constraint.
     */
    private isECommerceStoreAlreadyLinked(eCommerceStoreId: number, domain: string): boolean
    {
        const normalised = this.normaliseDomain(domain);
        return (this.businessECommerceStores ?? []).some(
            store => store.eCommerceStoreId === eCommerceStoreId && this.normaliseDomain(store.domain) === normalised);
    }

    /**
     * Mirrors the backend {@code DomainNormalizer}: strips scheme, leading {@code www.}, any path/query/fragment and a
     * trailing {@code :port}, and lower-cases — so the client comparison matches what the server would store.
     */
    private normaliseDomain(value: string | null | undefined): string
    {
        if (!value)
        {
            return '';
        }
        return value
            .trim()
            .toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .replace(/[/?#].*$/, '')
            .replace(/:\d+$/, '');
    }

    private handleApiError(error: any, defaultMessage: string): void
    {
        this.alertService.error(error?.error?.errorMessage || defaultMessage);
    }

    /**
     * Disables the selected e-commerce store.
     */
    private disableBusinessECommerceStore(businessECommerceStore: BusinessECommerceStore)
    {
        this.loading = true;

        this.webBusinessService.disableBusinessECommerceStore(businessECommerceStore.id)
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: () =>
                {
                    this.alertService.success(`Successfully disabled '${businessECommerceStore?.eCommerceStoreName}' e-commerce store.`);

                    this.loadECommerceStoresForBusiness();
                },
                error: (error: any) => this.handleApiError(
                    error,
                    'An unexpected error occurred. Please try again. If the issue persists, contact support.'
                )
            });
    }

    private getUpdatedFicaForm()
    {
        const ficaForm = (this.f.ficaDetails as UntypedFormGroup).getRawValue();
        if (this.otherNatureOfBusinessSelected)
        {
            ficaForm.natureOfBusiness = ficaForm.natureOfBusinessOther;
        }
        delete ficaForm['natureOfBusinessOther'];
        return ficaForm;
    }

    private resetMerchantOAuthClientSecret(merchantOauthClientSecretResetRequest: MerchantOauthClientSecretResetRequest)
    {
        this.webBusinessService.resetMerchantOAuthClientSecret(merchantOauthClientSecretResetRequest).subscribe({
            next: () =>
            {
                this.alertService.success('Successfully updated the client secret');
            },
            error: (error: any) => this.handleApiError(error, 'An unexpected error occurred.')
        });
    }
}
